import { Authenticated, HttpError, useCreate } from "@refinedev/core";
import { useTranslate } from "#src/hooks";
import { Flex, message, Result } from "antd";
import { useMediaAssetUpload } from "hooks/useMediaAssetUpload/";
import { useOrganization } from "hooks/useOrganization/";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  ContactCreationRequest,
  ContactResponse,
  FaceProfileCreationRequest,
  FaceProfileCreationResponse,
  PresenterProfileResponse,
} from "../../types";
import { CameraWithMask } from "./CameraWithMask";
import { ContentWrapper, MobileLayout } from "./MobileLayout";
import { PhotoSourceStep } from "./PhotoSourceStep";
import { FaceCloningConfirm } from "./FaceCloningConfirm";
import { VoiceStep } from "./VoiceStep";
import { useVoiceCloning } from "../../hooks/useVoiceCloning";
import { ConfirmStep } from "./ConfirmStep";
import { NameStep } from "./NameStep";
import { CTAControls } from "./controls/CTAControls";
import { CLONING_STEPS, useCloningSteps } from "./useCloningSteps";
import { ConfettiIcon } from "@phosphor-icons/react";
import { useAntTheme } from "hooks/useAntTheme/";

export const MobileCloningPage = () => {
  const [capturedPhoto, setCapturedPhoto] = useState<Blob | null>();
  const capturedPhotoUrlRef = useRef<string | null>(null);
  const [contactName, setContactName] = useState<string>("");
  const { organization } = useOrganization({});
  const [faceProfileId, setFaceProfileId] = useState<string | null>(null);
  const [voiceProfileId, setVoiceProfileId] = useState<string | null>(null);
  const [isCreatingFace, setIsCreatingFace] = useState(false);
  const [isCreatingContact, setIsCreatingContact] = useState(false);

  const t = useTranslate();
  const { theme } = useAntTheme();
  const url = new URL(globalThis.location.href);
  const isVoiceOnly = url.searchParams.get("voiceOnly") === "true";
  const [selectedLanguage, setSelectedLanguage] = useState<string | undefined>(
    undefined
  );

  const { mutateAsync: createPresenter } =
    useCreate<PresenterProfileResponse>();
  const { mutateAsync: createContact } = useCreate<
    ContactResponse,
    HttpError,
    ContactCreationRequest
  >();

  const {
    setRecordedSamples,
    isCreating: isCreatingVoice,
    recordedSamples,
    createVoiceProfile,
    isVoiceCloningComplete,
    clearAllSamples,
  } = useVoiceCloning({
    defaultName: contactName,
    language: selectedLanguage,
    onCreated: (voiceProfileId: string) => {
      setVoiceProfileId(voiceProfileId);
      setStep(CLONING_STEPS.CONFIRM);
    },
    autoCreateContact: false,
  });

  const canCreateContact = useMemo(
    () =>
      isVoiceOnly
        ? contactName && voiceProfileId
        : contactName && voiceProfileId && faceProfileId,
    [contactName, voiceProfileId, faceProfileId, isVoiceOnly]
  );

  const hasPhoto = !!capturedPhoto;
  const hasVoiceSamples = recordedSamples.length > 0;

  const isAllContactDataCollected = useMemo(() => {
    const hasVoiceContactData =
      contactName && voiceProfileId && canCreateContact;
    if (isVoiceOnly) {
      return !!hasVoiceContactData;
    } else {
      return !!(hasVoiceContactData && faceProfileId);
    }
  }, [
    contactName,
    voiceProfileId,
    faceProfileId,
    canCreateContact,
    isVoiceOnly,
  ]);

  const {
    step,
    setStep,
    showCamera,
    showNextButton,
    showConfirmButton,
    showBackButton,
    showResetButton,
    showResetPhotoText,
  } = useCloningSteps({
    hasPhoto,
    hasVoiceSamples,
    isAllContactDataCollected,
  });

  const handlePhotoCapture = useCallback(
    (blob: Blob) => {
      // Revoke previous URL if exists
      if (capturedPhotoUrlRef.current) {
        URL.revokeObjectURL(capturedPhotoUrlRef.current);
        capturedPhotoUrlRef.current = null;
      }
      setCapturedPhoto(blob);
      message.success(
        t("src.components.ContactCloningMobile.messages.photoCapturedSuccess")
      );
    },
    [t]
  );

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith("image/")) {
        message.error("Please select an image file");
        return;
      }

      // Convert file to blob and process it through the same crop function
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Create a canvas to crop the image to a circle
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");
          if (!context) return;

          // Use the smaller dimension for the circle size
          const size = Math.min(img.width, img.height);
          canvas.width = size;
          canvas.height = size;

          context.save();

          // Calculate center position for cropping (square crop)
          const sourceX = (img.width - size) / 2;
          const sourceY = (img.height - size) / 2;

          // Draw the image centered and cropped to square
          context.drawImage(
            img,
            sourceX,
            sourceY,
            size,
            size,
            0,
            0,
            size,
            size
          );

          context.restore();

          // Convert to blob
          canvas.toBlob(
            (blob) => {
              if (blob) {
                handlePhotoCapture(blob);
              }
            },
            "image/jpeg",
            0.8
          );
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);

      // Reset the input value so the same file can be selected again
      event.target.value = "";
    },
    [handlePhotoCapture]
  );

  // Derive the URL - create new one only when capturedPhoto changes
  const capturedPhotoUrl = useMemo(() => {
    if (capturedPhoto) {
      capturedPhotoUrlRef.current ??= URL.createObjectURL(capturedPhoto);
      return capturedPhotoUrlRef.current;
    }
    return null;
  }, [capturedPhoto]);

  const resetPhoto = () => {
    if (capturedPhotoUrlRef.current) {
      URL.revokeObjectURL(capturedPhotoUrlRef.current);
      capturedPhotoUrlRef.current = null;
    }
    setCapturedPhoto(null);
    setFaceProfileId(null);
    setStep(CLONING_STEPS.PHOTO_SOURCE);
  };

  const { uploadFiles, customRequest, createFileFromBlob } =
    useMediaAssetUpload({
      organization: organization?.id,
      assetType: "Picture",
      buildCreateAssetEndpoint(_file: File) {
        return `/media/${organization?.id}/face_profiles`;
      },
      buildCreateAssetParams(_file: File) {
        return {};
      },
      buildCreateAssetPayload(file: File): FaceProfileCreationRequest {
        return {
          name: contactName,
          category: "Custom",
          custom_image_filename: file.name,
        };
      },
      parseAsset(response: unknown) {
        return (response as { asset: unknown }).asset;
      },
    });

  const createFaceProfile = async (blob: Blob) => {
    try {
      // Use the createFileFromBlob utility from useMediaAssetUpload
      const file = createFileFromBlob(blob, "captured-face.jpg", "image/jpeg");

      // Add the file to the upload queue
      customRequest({
        file: file,
      });

      const responses = await uploadFiles<
        unknown,
        FaceProfileCreationResponse
      >();
      const { face_profile_id } = responses[0] ?? {};
      setFaceProfileId(face_profile_id ?? null);
      message.success(
        t("src.components.ContactCloningMobile.messages.faceProfileCreated")
      );
    } catch (error) {
      console.error("Error creating face profile:", error);
      message.error(
        t("src.components.ContactCloningMobile.messages.faceProfileError")
      );
      // Reset photo and step so user can try again
      if (capturedPhotoUrlRef.current) {
        URL.revokeObjectURL(capturedPhotoUrlRef.current);
        capturedPhotoUrlRef.current = null;
      }
      setCapturedPhoto(null);
      setStep(undefined);
    } finally {
      setIsCreatingFace(false);
    }
  };

  const createPresenterAndContact = useCallback(async () => {
    try {
      const presenterResponse = await createPresenter({
        resource: `media/${organization?.id}/presenter_profiles`,
        values: {
          name: contactName,
          ...(faceProfileId && { face_profile_id: faceProfileId }),
          voice_profile_id: voiceProfileId,
        },
        successNotification: false,
      });
      const { id: presenter_profile_id } = presenterResponse.data;
      await createContact({
        resource: `media/${organization?.id}/contacts`,
        values: {
          firstname: contactName,
          lastname: "",
          presenter_id: presenter_profile_id,
        },
        successNotification: false,
      });
      setTimeout(() => {
        setStep(CLONING_STEPS.READY);
      }, 1000);
    } catch (error) {
      console.error("Error creating presenter:", error);
      message.error(
        t("src.components.ContactCloningMobile.errors.errorPresenterCreation")
      );
    }
  }, [
    createPresenter,
    createContact,
    organization?.id,
    contactName,
    faceProfileId,
    voiceProfileId,
    setStep,
    t,
  ]);

  const handleConfirmPhoto = async () => {
    if (!capturedPhoto) return;
    setIsCreatingFace(true);
    await createFaceProfile(capturedPhoto);
    setStep(CLONING_STEPS.VOICE);
  };

  const handleNextClick = () => {
    if (!step) {
      setStep(isVoiceOnly ? CLONING_STEPS.VOICE : CLONING_STEPS.PHOTO_SOURCE);
    }
    if (
      (step === CLONING_STEPS.CAMERA || step === CLONING_STEPS.PHOTO_SOURCE) &&
      capturedPhotoUrl
    ) {
      if (faceProfileId) {
        setStep(CLONING_STEPS.VOICE);
      } else {
        handleConfirmPhoto().catch((error) => {
          console.error("Error creating face profile:", error);
        });
      }
    }

    if (step === CLONING_STEPS.VOICE) {
      if (voiceProfileId) {
        setStep(CLONING_STEPS.CONFIRM);
      } else {
        createVoiceProfile().catch((error) => {
          console.error("Error creating voice profile:", error);
        });
      }
    }
  };

  const handleBackClick = () => {
    if (step === CLONING_STEPS.PHOTO_SOURCE) {
      setStep(undefined);
    } else if (step === CLONING_STEPS.VOICE) {
      if (isVoiceOnly) {
        setStep(undefined);
      } else {
        setStep(CLONING_STEPS.PHOTO_SOURCE);
      }
    } else if (step === CLONING_STEPS.CONFIRM) {
      setStep(CLONING_STEPS.VOICE);
    }
  };

  const handleResetClick = () => {
    if (step === "voice") {
      setRecordedSamples([]);
      setVoiceProfileId(null);
    } else {
      resetPhoto();
    }
  };

  const handleConfirmClick = () => {
    setIsCreatingContact(true);
  };

  useEffect(() => {
    if (isVoiceCloningComplete && isCreatingContact) {
      createPresenterAndContact()
        .catch((error) => {
          console.error("Error creating presenter and contact:", error);
        })
        .finally(() => {
          setIsCreatingContact(false);
        });
    }
  }, [isVoiceCloningComplete, isCreatingContact]);

  const renderContent = () => {
    if (step === CLONING_STEPS.PHOTO_SOURCE && !capturedPhoto) {
      return (
        <PhotoSourceStep
          onCameraSelect={() => setStep(CLONING_STEPS.CAMERA)}
          onLibrarySelect={() => {
            // This will be called when a file is selected from the built-in file input
            // The actual file handling happens in the PhotoSourceSelector component
          }}
          onFileSelect={handleFileSelect}
        />
      );
    }
    if (step === CLONING_STEPS.VOICE) {
      return (
        <VoiceStep
          recordedSamples={recordedSamples}
          setRecordedSamples={setRecordedSamples}
          isCreating={isCreatingVoice}
          selectedLanguage={selectedLanguage}
          setSelectedLanguage={setSelectedLanguage}
          clearAllSamples={clearAllSamples}
        />
      );
    }
    if (step === CLONING_STEPS.CONFIRM && capturedPhotoUrl) {
      return (
        <ConfirmStep
          name={contactName}
          faceUrl={capturedPhotoUrl}
          recordedSamples={recordedSamples}
        />
      );
    }

    if (
      (step === CLONING_STEPS.CAMERA || step === CLONING_STEPS.PHOTO_SOURCE) &&
      capturedPhoto
    ) {
      if (!capturedPhotoUrl) return null;
      return <FaceCloningConfirm capturedPhotoUrl={capturedPhotoUrl} />;
    }

    if (step === CLONING_STEPS.READY) {
      return (
        <Result
          status="success"
          icon={<ConfettiIcon size={60} color={theme.colorPrimary} />}
          title={t(
            "src.components.ContactCloningMobile.messages.contactCreatedSuccess"
          )}
          subTitle={t(
            "src.components.ContactCloningMobile.messages.proceedOnDesktop"
          )}
        />
      );
    }

    return (
      <NameStep
        contactName={contactName}
        setContactName={setContactName}
        isVoiceOnly={isVoiceOnly}
      />
    );
  };

  return (
    <Authenticated key="contact-mobile-cloning">
      {showCamera && (
        <CameraWithMask
          onPhotoCapture={handlePhotoCapture}
          onCancel={() => setStep(CLONING_STEPS.PHOTO_SOURCE)}
        />
      )}
      {!showCamera && (
        <MobileLayout>
          <Flex
            vertical
            gap={24}
            align="center"
            style={{ width: "100%", flex: 1, minHeight: 0 }}
          >
            <ContentWrapper showTitle={step !== CLONING_STEPS.READY}>
              {renderContent()}
            </ContentWrapper>
            <CTAControls
              showNextButton={showNextButton}
              showConfirmButton={showConfirmButton}
              showBackButton={showBackButton}
              showResetButton={showResetButton}
              showResetPhotoText={showResetPhotoText}
              isNextLoading={isCreatingFace || isCreatingVoice}
              isConfirmLoading={isCreatingContact}
              isNextDisabled={!contactName}
              onNextClick={handleNextClick}
              onConfirmClick={handleConfirmClick}
              onBackClick={handleBackClick}
              onResetClick={handleResetClick}
            />
          </Flex>
        </MobileLayout>
      )}
    </Authenticated>
  );
};
